import { useState, useMemo, useId, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
  ShieldOff,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CalendarDays,
} from 'lucide-react';
import {
  format,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isToday,
  isSameMonth,
} from 'date-fns';
import { UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import {
  usePricingRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  useOverridePrice,
  useAuditLog,
} from '@/hooks/api/usePricing';
import { useProperties } from '@/hooks/api/useProperties';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';

// ---- Constants ----

type RuleType = 'weekday' | 'weekend' | 'occupancy' | 'seasonal' | 'manual';
type AdjustmentType = 'percent' | 'fixed';
type ConditionOperator = 'gt' | 'lt' | 'gte' | 'lte';

const RULE_TYPE_LABELS: Record<RuleType, string> = {
  weekday: 'Weekday',
  weekend: 'Weekend',
  occupancy: 'Occupancy',
  seasonal: 'Seasonal',
  manual: 'Manual Override',
};

const RULE_TYPE_COLORS: Record<RuleType, string> = {
  weekday: 'bg-blue-100 text-blue-700',
  weekend: 'bg-violet-100 text-violet-700',
  occupancy: 'bg-amber-100 text-amber-700',
  seasonal: 'bg-emerald-100 text-emerald-700',
  manual: 'bg-rose-100 text-rose-700',
};

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  gt: '>',
  lt: '<',
  gte: '>=',
  lte: '<=',
};

// ---- Helpers ----

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function fmtDate(iso: string): string {
  try { return format(parseISO(iso), 'MMM d, yyyy'); } catch { return iso; }
}

function formatAdjustment(type: AdjustmentType, value: number): string {
  if (type === 'percent') return `${value > 0 ? '+' : ''}${value}%`;
  return `${value >= 0 ? '+' : ''}${fmt(value)}`;
}

function formatCondition(operator: ConditionOperator | null, value: number | null): string {
  if (!operator || value == null) return '\u2014';
  return `Occupancy ${OPERATOR_LABELS[operator]} ${(value * 100).toFixed(0)}%`;
}

// ---- Access Denied Card ----

function AccessDeniedCard() {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-white py-24 px-6 text-center gap-4">
      <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50">
        <ShieldOff size={32} className="text-red-400" aria-hidden="true" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400 max-w-sm">
          Pricing management is restricted to Admins and Revenue Managers.
        </p>
      </div>
    </div>
  );
}

// ---- Rule Form (Slide-over) ----

interface RuleFormData {
  unitId: string;
  ruleType: RuleType;
  conditionOperator: ConditionOperator | '';
  conditionValue: string;
  adjustmentType: AdjustmentType;
  adjustmentValue: string;
  priority: string;
  validFrom: string;
  validTo: string;
}

const EMPTY_FORM: RuleFormData = {
  unitId: '',
  ruleType: 'weekday',
  conditionOperator: '',
  conditionValue: '',
  adjustmentType: 'percent',
  adjustmentValue: '',
  priority: '1',
  validFrom: '',
  validTo: '',
};

function RuleSlideOver({
  isOpen,
  editingRule,
  onClose,
  onSave,
  units,
}: {
  isOpen: boolean;
  editingRule: Record<string, unknown> | null;
  onClose: () => void;
  onSave: (data: RuleFormData, editId: string | null) => void;
  units: Array<Record<string, unknown>>;
}) {
  const headingId = useId();

  const initial: RuleFormData = editingRule
    ? {
        unitId: (editingRule.unitId as string) ?? '',
        ruleType: (editingRule.ruleType as RuleType) ?? 'weekday',
        conditionOperator: (editingRule.conditionOperator as ConditionOperator) ?? '',
        conditionValue: editingRule.conditionValue != null ? String(editingRule.conditionValue) : '',
        adjustmentType: (editingRule.adjustmentType as AdjustmentType) ?? 'percent',
        adjustmentValue: String(editingRule.adjustmentValue ?? ''),
        priority: String(editingRule.priority ?? '1'),
        validFrom: (editingRule.validFrom as string) ?? '',
        validTo: (editingRule.validTo as string) ?? '',
      }
    : { ...EMPTY_FORM, unitId: units[0]?.id as string ?? '' };

  const [form, setForm] = useState<RuleFormData>(initial);

  function handleClose() {
    setForm(editingRule ? initial : { ...EMPTY_FORM, unitId: units[0]?.id as string ?? '' });
    onClose();
  }

  function set(key: keyof RuleFormData, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave(form, (editingRule?.id as string) ?? null);
    handleClose();
  }

  const needsCondition = form.ruleType === 'occupancy';
  const needsDates = form.ruleType === 'seasonal';

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" aria-hidden="true" onClick={handleClose} />
      <aside role="dialog" aria-modal="true" aria-labelledby={headingId} className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h2 id={headingId} className="text-base font-semibold text-gray-900">{editingRule ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</h2>
          <button onClick={handleClose} aria-label="Close" className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Unit</label>
            <select value={form.unitId} onChange={(e) => set('unitId', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required>
              {units.map((u) => (
                <option key={u.id as string} value={u.id as string}>
                  {(u.propertyName as string) ?? ''} {'\u2014'} {(u.name as string) ?? ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Rule Type</label>
            <select value={form.ruleType} onChange={(e) => set('ruleType', e.target.value as RuleType)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required>
              {(Object.keys(RULE_TYPE_LABELS) as RuleType[]).map((rt) => (
                <option key={rt} value={rt}>{RULE_TYPE_LABELS[rt]}</option>
              ))}
            </select>
          </div>

          {needsCondition && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Operator</label>
                <select value={form.conditionOperator} onChange={(e) => set('conditionOperator', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required={needsCondition}>
                  <option value="">Select</option>
                  <option value="gt">{'> (greater than)'}</option>
                  <option value="gte">{'>= (greater or equal)'}</option>
                  <option value="lt">{'< (less than)'}</option>
                  <option value="lte">{'<= (less or equal)'}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Occupancy (0-1)</label>
                <input type="number" step="0.01" min="0" max="1" value={form.conditionValue} onChange={(e) => set('conditionValue', e.target.value)} placeholder="e.g. 0.8" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required={needsCondition} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Adjustment Type</label>
              <select value={form.adjustmentType} onChange={(e) => set('adjustmentType', e.target.value as AdjustmentType)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required>
                <option value="percent">Percent (%)</option>
                <option value="fixed">Fixed ($)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Value</label>
              <input type="number" step="0.01" value={form.adjustmentValue} onChange={(e) => set('adjustmentValue', e.target.value)} placeholder={form.adjustmentType === 'percent' ? 'e.g. 15 or -10' : 'e.g. 20 or -25'} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Priority (higher = applied first)</label>
            <input type="number" min="1" max="100" value={form.priority} onChange={(e) => set('priority', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
          </div>

          {needsDates && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Valid From</label>
                <input type="date" value={form.validFrom} onChange={(e) => set('validFrom', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required={needsDates} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Valid To</label>
                <input type="date" value={form.validTo} onChange={(e) => set('validTo', e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required={needsDates} />
              </div>
            </div>
          )}

          <div className="pt-2">
            <button type="submit" className="w-full px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
              {editingRule ? 'Save Changes' : 'Create Rule'}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
}

// ---- Price Override Form ----

function PriceOverrideForm({ units }: { units: Array<Record<string, unknown>> }) {
  const overrideMutation = useOverridePrice();
  const [form, setForm] = useState({ unitId: units[0]?.id as string ?? '', newPrice: '', reason: '' });

  function set(key: string, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    overrideMutation.mutate({
      unitId: form.unitId,
      newPrice: parseFloat(form.newPrice),
      reason: form.reason,
    });
    setForm({ unitId: units[0]?.id as string ?? '', newPrice: '', reason: '' });
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-gray-800 mb-4">Price Override</h2>
      {overrideMutation.isSuccess && (
        <p role="status" aria-live="polite" className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5">
          Override submitted successfully.
        </p>
      )}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Unit</label>
          <div className="relative">
            <select value={form.unitId} onChange={(e) => set('unitId', e.target.value)} className="w-full appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required>
              {units.map((u) => (
                <option key={u.id as string} value={u.id as string}>
                  {(u.propertyName as string) ?? ''} {'\u2014'} {(u.name as string) ?? ''}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">New Base Price (USD/night)</label>
          <input type="number" step="0.01" min="0" value={form.newPrice} onChange={(e) => set('newPrice', e.target.value)} placeholder="e.g. 250.00" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Reason</label>
          <input type="text" value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Event week premium" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" required />
        </div>
        <div className="sm:col-span-3">
          <button type="submit" disabled={overrideMutation.isPending} className="px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
            {overrideMutation.isPending ? 'Applying...' : 'Apply Override'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---- Audit Log Table ----

function AuditLogTable() {
  const { data, isLoading } = useAuditLog();
  const entries: Array<Record<string, unknown>> = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : (data.data ?? []);
  }, [data]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">Pricing Audit Log</h2>
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-primary-600" />
          <span className="ml-2 text-sm text-gray-500">Loading audit log...</span>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Changed By</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Old Price</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">New Price</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((entry) => (
                <tr key={entry.id as string} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3.5 text-gray-600 whitespace-nowrap text-xs">{fmtDate((entry.createdAt as string) ?? '')}</td>
                  <td className="px-4 py-3.5 font-medium text-gray-800">{(entry.unitName as string) ?? ''}</td>
                  <td className="px-4 py-3.5 text-gray-600 text-xs">{(entry.changedBy as string) ?? ''}</td>
                  <td className="px-4 py-3.5 text-right text-gray-500">{fmt((entry.oldPrice as number) ?? 0)}</td>
                  <td className="px-4 py-3.5 text-right font-semibold">
                    <span className={(entry.newPrice as number) > (entry.oldPrice as number) ? 'text-emerald-700' : 'text-red-600'}>
                      {fmt((entry.newPrice as number) ?? 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-gray-600 text-xs max-w-xs truncate">{(entry.reason as string) ?? '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---- Rate Calendar ----

type CellOverride = Record<string, Record<string, number>>; // { [unitId]: { [YYYY-MM-DD]: price } }

function RateCalendarTab({ units }: { units: Array<Record<string, unknown>> }) {
  const today = useMemo(() => new Date(), []);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(today));
  const [filterUnitId, setFilterUnitId] = useState<string>('all');
  const [overrides, setOverrides] = useState<CellOverride>({});
  const [editCell, setEditCell] = useState<{ unitId: string; date: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  const overrideMutation = useOverridePrice();
  const filterSelectId = useId();

  const days = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) }),
    [currentMonth],
  );

  const visibleUnits = useMemo(() => {
    if (filterUnitId === 'all') return units;
    return units.filter((u) => (u.id as string) === filterUnitId);
  }, [units, filterUnitId]);

  function prevMonth() {
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }
  function goToday() {
    setCurrentMonth(startOfMonth(today));
  }

  function openEdit(unitId: string, dateStr: string, basePrice: number) {
    const existing = overrides[unitId]?.[dateStr];
    setEditCell({ unitId, date: dateStr });
    setEditValue(String(existing ?? basePrice));
  }

  function closeEdit() {
    setEditCell(null);
    setEditValue('');
  }

  const handleCommitEdit = useCallback(
    (unitId: string, date: string) => {
      const price = parseFloat(editValue);
      if (!isNaN(price) && price >= 0) {
        setOverrides((prev) => ({
          ...prev,
          [unitId]: { ...(prev[unitId] ?? {}), [date]: price },
        }));
        overrideMutation.mutate(
          { unitId, newPrice: price, reason: `Manual rate override for ${date}` },
        );
      }
      closeEdit();
    },
    [editValue, overrideMutation],
  );

  if (units.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <CalendarDays size={32} className="text-gray-300" aria-hidden="true" />
        <p className="text-sm text-gray-400">No units available. Add a property and units first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Month navigator */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={prevMonth}
            aria-label="Previous month"
            className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span className="text-sm font-semibold text-gray-800 min-w-[110px] text-center">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
          <button
            type="button"
            onClick={nextMonth}
            aria-label="Next month"
            className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="ml-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            Today
          </button>
        </div>

        {/* Unit filter */}
        <div className="relative ml-auto">
          <label htmlFor={filterSelectId} className="sr-only">Filter by unit</label>
          <select
            id={filterSelectId}
            value={filterUnitId}
            onChange={(e) => setFilterUnitId(e.target.value)}
            className="appearance-none rounded-lg border border-gray-300 bg-white px-3 py-1.5 pr-8 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Units</option>
            {units.map((u) => (
              <option key={u.id as string} value={u.id as string}>
                {(u.propertyName as string) ?? ''} — {(u.name as string) ?? ''}
              </option>
            ))}
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-100 border border-green-200" aria-hidden="true" />Above base</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-100 border border-red-200" aria-hidden="true" />Below base</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-gray-200" aria-hidden="true" />Base rate</span>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs" aria-label="Rate calendar">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th
                  scope="col"
                  className="sticky left-0 bg-gray-50 z-10 px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap min-w-[160px] border-r border-gray-200"
                >
                  Unit
                </th>
                {days.map((day) => (
                  <th
                    key={day.toISOString()}
                    scope="col"
                    className={cn(
                      'px-1.5 py-3 text-center font-medium whitespace-nowrap min-w-[52px]',
                      isToday(day) ? 'text-primary-600 bg-primary-50' : 'text-gray-500',
                    )}
                  >
                    <div>{format(day, 'd')}</div>
                    <div className="text-gray-400 font-normal">{format(day, 'EEE')}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleUnits.map((unit) => {
                const unitId = unit.id as string;
                const basePrice = typeof unit.basePrice === 'number'
                  ? unit.basePrice
                  : parseFloat(String(unit.basePrice ?? '0')) || 0;

                return (
                  <tr key={unitId} className="hover:bg-gray-50/50 transition-colors">
                    <td className="sticky left-0 bg-white border-r border-gray-100 px-4 py-2 whitespace-nowrap z-10">
                      <div className="font-medium text-gray-800 text-xs">{unit.name as string}</div>
                      <div className="text-gray-400 text-xs">{unit.propertyName as string}</div>
                    </td>
                    {days.map((day) => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const overridePrice = overrides[unitId]?.[dateStr];
                      const displayPrice = overridePrice ?? basePrice;
                      const isEditing = editCell?.unitId === unitId && editCell?.date === dateStr;
                      const isAbove = overridePrice != null && overridePrice > basePrice;
                      const isBelow = overridePrice != null && overridePrice < basePrice;
                      const isCurrentMonth = isSameMonth(day, currentMonth);

                      if (!isCurrentMonth) {
                        return (
                          <td key={dateStr} className="px-1.5 py-2 text-center bg-gray-50/50">
                            <span className="text-gray-300">—</span>
                          </td>
                        );
                      }

                      return (
                        <td
                          key={dateStr}
                          className={cn(
                            'px-1 py-1 text-center align-middle',
                            isToday(day) && 'bg-primary-50/30',
                          )}
                        >
                          {isEditing ? (
                            <div className="flex items-center gap-0.5">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleCommitEdit(unitId, dateStr)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleCommitEdit(unitId, dateStr);
                                  if (e.key === 'Escape') closeEdit();
                                }}
                                autoFocus
                                aria-label={`Rate for ${unit.name as string} on ${dateStr}`}
                                className="w-14 rounded border border-primary-400 px-1 py-0.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openEdit(unitId, dateStr, basePrice)}
                              title={`Click to override rate for ${dateStr}`}
                              aria-label={`Rate £${displayPrice} for ${unit.name as string} on ${dateStr}. Click to edit.`}
                              className={cn(
                                'w-full rounded px-1 py-1 text-xs font-medium transition-colors hover:ring-1 hover:ring-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                isAbove ? 'bg-green-50 text-green-800' : isBelow ? 'bg-red-50 text-red-700' : 'bg-white text-gray-700',
                              )}
                            >
                              £{displayPrice}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Click any cell to override the rate for that unit and date. Changes are saved immediately.
      </p>
    </div>
  );
}

// ---- Tab types ----

type PricingTabId = 'rules' | 'rate-calendar';

const PRICING_TABS: { id: PricingTabId; label: string }[] = [
  { id: 'rules', label: 'Pricing Rules' },
  { id: 'rate-calendar', label: 'Rate Calendar' },
];

// ---- Main Page ----

export function PricingPage() {
  const { user } = useAuth();
  const isAllowed = user?.role === UserRole.ADMIN || user?.role === UserRole.REVENUE_MANAGER;
  const propertyId = useSelectedPropertyId();

  const [activeTab, setActiveTab] = useState<PricingTabId>('rules');
  const [selectedUnitId, setSelectedUnitId] = useState<string>('all');
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Record<string, unknown> | null>(null);

  const { data: rulesData, isLoading: rulesLoading } = usePricingRules();
  const { data: propertiesData } = useProperties();
  const createRuleMutation = useCreateRule();
  const updateRuleMutation = useUpdateRule();
  const deleteRuleMutation = useDeleteRule();

  const rules: Array<Record<string, unknown>> = useMemo(() => {
    if (!rulesData) return [];
    return Array.isArray(rulesData) ? rulesData : (rulesData.data ?? []);
  }, [rulesData]);

  // Derive units from properties for selectors, scoped to selected property
  const units: Array<Record<string, unknown>> = useMemo(() => {
    if (!propertiesData) return [];
    const props = Array.isArray(propertiesData) ? propertiesData : (propertiesData.data ?? []);
    const filtered = propertyId ? props.filter((p: Record<string, unknown>) => p.id === propertyId) : props;
    return filtered.flatMap((p: Record<string, unknown>) => {
      const propUnits = Array.isArray(p.units) ? p.units : [];
      return propUnits.map((u: Record<string, unknown>) => ({
        ...u,
        propertyName: p.name,
        propertyId: p.id,
      }));
    });
  }, [propertiesData, propertyId]);

  const filteredRules = useMemo(() => {
    let result = rules;
    // Scope rules to units belonging to the selected property
    if (propertyId && units.length > 0) {
      const unitIds = new Set(units.map((u) => u.id as string));
      result = result.filter((r) => unitIds.has(r.unitId as string));
    }
    if (selectedUnitId !== 'all') {
      result = result.filter((r) => r.unitId === selectedUnitId);
    }
    return result;
  }, [rules, selectedUnitId, propertyId, units]);

  if (!isAllowed) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Pricing</h1>
          <p className="text-sm text-gray-500 mt-0.5">Dynamic pricing rules and rate management</p>
        </div>
        <AccessDeniedCard />
      </div>
    );
  }

  function handleSaveRule(data: RuleFormData, editId: string | null) {
    const payload = {
      unitId: data.unitId,
      ruleType: data.ruleType,
      conditionOperator: (data.conditionOperator as ConditionOperator) || null,
      conditionValue: data.conditionValue ? parseFloat(data.conditionValue) : null,
      adjustmentType: data.adjustmentType,
      adjustmentValue: parseFloat(data.adjustmentValue),
      priority: parseInt(data.priority, 10),
      validFrom: data.validFrom || null,
      validTo: data.validTo || null,
    };

    if (editId) {
      updateRuleMutation.mutate({ id: editId, payload });
    } else {
      createRuleMutation.mutate(payload);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-0">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Pricing</h1>
          <p className="text-sm text-gray-500 mt-0.5">Dynamic pricing rules and rate management</p>
        </div>
      </div>

      {/* Tab navigation */}
      <nav aria-label="Pricing tabs">
        <div className="flex gap-1 border-b border-gray-200" role="tablist">
          {PRICING_TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              id={`pricing-tab-${tab.id}`}
              aria-selected={activeTab === tab.id}
              aria-controls={`pricing-panel-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors rounded-t-lg',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Rate Calendar tab */}
      {activeTab === 'rate-calendar' && (
        <div role="tabpanel" id="pricing-panel-rate-calendar" aria-labelledby="pricing-tab-rate-calendar">
          <RateCalendarTab units={units} />
        </div>
      )}

      {/* Pricing Rules tab */}
      {activeTab === 'rules' && (
      <div role="tabpanel" id="pricing-panel-rules" aria-labelledby="pricing-tab-rules">

      {/* Rules section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-800">Active Pricing Rules</h2>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {rules.filter((r) => r.isActive).length} active
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <select value={selectedUnitId} onChange={(e) => setSelectedUnitId(e.target.value)} aria-label="Filter by unit" className="appearance-none rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                <option value="all">All Units</option>
                {units.map((u) => (
                  <option key={u.id as string} value={u.id as string}>
                    {(u.propertyName as string) ?? ''} - {(u.name as string) ?? ''}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
            </div>
            <button onClick={() => { setEditingRule(null); setSlideOverOpen(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
              <Plus size={15} aria-hidden="true" />
              Add Rule
            </button>
          </div>
        </div>

        {rulesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={20} className="animate-spin text-primary-600" />
            <span className="ml-2 text-sm text-gray-500">Loading rules...</span>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-400">No pricing rules found for the selected unit.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Condition</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Adjustment</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Priority</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Valid Period</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRules.map((rule) => (
                  <tr key={rule.id as string} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', RULE_TYPE_COLORS[(rule.ruleType as RuleType)] ?? 'bg-gray-100 text-gray-600')}>
                        {RULE_TYPE_LABELS[(rule.ruleType as RuleType)] ?? (rule.ruleType as string)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700">
                      <span className="font-medium">{(rule.unitName as string) ?? ''}</span>
                      <span className="text-gray-400 text-xs ml-1">({(rule.propertyName as string) ?? ''})</span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-600">
                      {rule.ruleType === 'occupancy' ? formatCondition(rule.conditionOperator as ConditionOperator | null, rule.conditionValue as number | null) : '\u2014'}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className={cn('font-semibold', (rule.adjustmentValue as number) > 0 ? 'text-emerald-700' : 'text-red-600')}>
                        {formatAdjustment(rule.adjustmentType as AdjustmentType, rule.adjustmentValue as number)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right text-gray-600">{rule.priority as number}</td>
                    <td className="px-4 py-3.5 text-gray-600 text-xs">
                      {rule.validFrom && rule.validTo ? `${fmtDate(rule.validFrom as string)} \u2013 ${fmtDate(rule.validTo as string)}` : '\u2014'}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setEditingRule(rule); setSlideOverOpen(true); }} aria-label={`Edit rule for ${(rule.unitName as string) ?? ''}`} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors">
                          <Pencil size={15} aria-hidden="true" />
                        </button>
                        <button onClick={() => { if (!window.confirm('Delete this item?')) return; deleteRuleMutation.mutate(rule.id as string); }} aria-label={`Delete rule for ${(rule.unitName as string) ?? ''}`} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 transition-colors">
                          <Trash2 size={15} aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PriceOverrideForm units={units} />
      <AuditLogTable />

      </div>
      )}

      <RuleSlideOver isOpen={slideOverOpen} editingRule={editingRule} onClose={() => setSlideOverOpen(false)} onSave={handleSaveRule} units={units} />
    </div>
  );
}
